import { useBlueprints } from './useBlueprints';
import { BUILDING_STATUSES } from './useBlueprintFilters';
import type { Blueprint, BlueprintStatus } from '@/lib/types';

export interface BaseBlueprintBuildingState {
  isBuilding: boolean;
  status: BlueprintStatus | null;
  blueprint: Blueprint | null;
}

/**
 * Returns the building state of the per-org base blueprint when no
 * completed blueprint exists yet. This is the "first launch" gating
 * signal — the launch dialog shows a friendly notice and disables
 * submission while this is true. Once any `build_complete` blueprint
 * lands in the cache (the per-org base finishes, or any other
 * completed blueprint exists), the flag flips back to `false`.
 *
 * Auto-recovery relies on the 5s `refetchInterval` already wired into
 * `useBlueprints` when any row is in a building state.
 */
export function useBaseBlueprintBuilding(): BaseBlueprintBuildingState {
  const { data: blueprints = [] } = useBlueprints();
  const hasComplete = blueprints.some((bp) => bp.status === 'build_complete');
  if (hasComplete) return { isBuilding: false, status: null, blueprint: null };
  const building = blueprints.find(
    (bp) => bp.name === 'base' && BUILDING_STATUSES.includes(bp.status),
  );
  return building
    ? { isBuilding: true, status: building.status, blueprint: building }
    : { isBuilding: false, status: null, blueprint: null };
}
